const IMAGE_URL_EXTENSIONS = /\.(jpe?g|png|gif|webp|bmp|ico|avif)(\?[^#]*)?$/i;

const OPENCLAW_MEDIA_PREFIXES = [
  '/home/denny/.openclaw/media/',
  '/home/denny/.openclaw/workspace/media/',
];

export function normalizeGeneratedImageUrl(raw) {
  if (!raw || typeof raw !== 'string') return null;
  let value = raw.trim();
  if (!value) return null;

  if (value.startsWith('data:image/')) return value;

  for (const prefix of OPENCLAW_MEDIA_PREFIXES) {
    if (value.startsWith(prefix)) {
      return `/media/${value.slice(prefix.length)}`;
    }
  }

  if (value.startsWith('/media/')) return value;
  if (value.startsWith('media/')) return `/${value}`;

  try {
    const url = new URL(value);
    if ((url.protocol === 'http:' || url.protocol === 'https:') && IMAGE_URL_EXTENSIONS.test(url.pathname)) {
      return value;
    }
  } catch {
    // Not an absolute URL.
  }

  if (/^[A-Za-z0-9._/-]+\.(?:jpe?g|png|gif|webp|bmp|ico|avif)(?:\?[^#]*)?$/i.test(value)) {
    return value.startsWith('/') ? value : `/media/${value}`;
  }

  return null;
}

function isImageLikeObject(obj) {
  if (!obj || typeof obj !== 'object') return false;
  const type = String(obj.type || obj.mime_type || obj.media_type || obj.content_type || '').toLowerCase();
  return type.includes('image') || type === 'output_image' || type === 'image_generation_call';
}

export function collectGeneratedImageUrls(value, seenObjects = new WeakSet(), parentIsImage = false) {
  const urls = [];
  const add = (url) => {
    if (url && !urls.includes(url)) urls.push(url);
  };

  if (!value) return urls;

  if (typeof value === 'string') {
    if (parentIsImage) add(normalizeGeneratedImageUrl(value));
    return urls;
  }

  if (Array.isArray(value)) {
    value.forEach((item) => {
      collectGeneratedImageUrls(item, seenObjects, parentIsImage).forEach(add);
    });
    return urls;
  }

  if (typeof value !== 'object') return urls;
  if (seenObjects.has(value)) return urls;
  seenObjects.add(value);

  const currentIsImage = parentIsImage || isImageLikeObject(value);

  for (const [key, child] of Object.entries(value)) {
    const keyLower = key.toLowerCase();

    if (typeof child === 'string') {
      if (keyLower === 'b64_json' || (currentIsImage && /^(base64|data)$/.test(keyLower))) {
        const mime = value.mime_type || value.media_type || value.content_type || 'image/png';
        add(child.startsWith('data:image/') ? child : `data:${mime};base64,${child}`);
        continue;
      }

      if (
        currentIsImage ||
        /^(url|image_url|src|href|path|file_path|filename|output_path|local_path)$/.test(keyLower)
      ) {
        add(normalizeGeneratedImageUrl(child));
      }
      continue;
    }

    collectGeneratedImageUrls(child, seenObjects, currentIsImage).forEach(add);
  }

  return urls;
}

export function markdownForGeneratedImages(urls) {
  if (!urls.length) return '';
  return urls.map((url, index) => `![Generated image ${index + 1}](${url})`).join('\n\n');
}
